import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { Spin } from 'antd';
import { useState } from 'react';

import { Button } from '@/components/common/button';
import { useToast } from '@/components/feedback/toast';

import { getInvoiceUrl } from '../api';
import { isFinalStatus } from '../constants';
import { useOrder } from '../hooks';

import { AddParametersModal } from './add-parameters-modal';
import { ExpectedDeliveryField } from './expected-delivery-field';
import { LogisticsCard } from './logistics-card';
import { OrderActionsMenu } from './order-actions-menu';
import { OrderDetailHeader } from './order-detail-header';
import styles from './order-detail.module.scss';
import { OrderDetailsCard } from './order-details-card';
import { OrderStatusControl } from './order-status-control';
import { OrderSummaryCard } from './order-summary-card';
import { RaiseTicketDrawer } from './raise-ticket-drawer';
import { RequestReplacementDrawer } from './request-replacement-drawer';
import { ReviewsDrawer } from './reviews-drawer';

type Overlay =
  | { kind: 'ticket' }
  | { kind: 'review' }
  | { kind: 'replacement' }
  | { kind: 'params'; lineItemId: string | null }
  | null;

/** Trigger a client-side placeholder download (no real PDF in the prototype). */
function downloadInvoicePlaceholder(orderNumber: string) {
  const blob = new Blob([`Invoice for order #${orderNumber}\n\n(Prototype placeholder)`], {
    type: 'application/pdf',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Invoice_#${orderNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function OrderDetailView({ orderId }: { orderId: string }) {
  const { data: order, isLoading, isError } = useOrder(orderId);
  const toast = useToast();
  const [overlay, setOverlay] = useState<Overlay>(null);
  const close = () => setOverlay(null);

  if (isLoading) {
    return (
      <div className={styles.center}>
        <Spin />
      </div>
    );
  }
  if (isError || !order) {
    return <div className={styles.center}>Order not found.</div>;
  }

  const handleInvoice = async () => {
    try {
      await getInvoiceUrl(order.id);
      downloadInvoicePlaceholder(order.orderNumber);
    } catch {
      toast.error({ title: 'Could not download the invoice. Please try again.' });
    }
  };

  const actions = (
    <>
      <OrderStatusControl order={order} />
      <Button
        variant="outline"
        startIcon={<DownloadSimpleIcon size={18} />}
        disabled={!order.orderMeta.paymentComplete}
        onClick={handleInvoice}
        style={{ backgroundColor: '#FEFEFE' }}
      >
        Invoice
      </Button>
      <OrderActionsMenu
        order={order}
        onRaiseTicket={() => setOverlay({ kind: 'ticket' })}
        onReadReview={() => setOverlay({ kind: 'review' })}
        onRequestReplacement={() => setOverlay({ kind: 'replacement' })}
        onAddParameters={(lineItemId) => setOverlay({ kind: 'params', lineItemId })}
      />
    </>
  );

  return (
    <div className={styles.page}>
      <OrderDetailHeader order={order} actions={actions} />
      <div className={styles.body}>
        <OrderSummaryCard
          lineItems={order.lineItems}
          totals={order.totals}
          onAddParameters={(lineItemId) => setOverlay({ kind: 'params', lineItemId })}
        />
        <div className={styles.sidebar}>
          <OrderDetailsCard
            order={order}
            deliveryField={
              isFinalStatus(order.status) ? undefined : <ExpectedDeliveryField order={order} />
            }
          />
          <LogisticsCard logistics={order.logistics} />
        </div>
      </div>

      {overlay?.kind === 'ticket' && <RaiseTicketDrawer order={order} open onClose={close} />}
      {overlay?.kind === 'review' && <ReviewsDrawer order={order} open onClose={close} />}
      {overlay?.kind === 'replacement' && (
        <RequestReplacementDrawer order={order} open onClose={close} />
      )}
      {overlay?.kind === 'params' && (
        <AddParametersModal order={order} lineItemId={overlay.lineItemId} open onClose={close} />
      )}
    </div>
  );
}
